import EvidenceModal from "custom_modules/TasksNew/TaskDetails/EvidenceModal";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteButton, ModalDeleteButton } from "styledComponents/buttons";

import avatar5 from "../../assets/img/avatar_5.svg";
import ComponentCircleLoader from "../../components/Loaders/ComponentCircleLoader";
import NoDataComponent from "../../components/NoDataComponent/NoDataComponent";
import InfoTooltip from "../../components/Tooltip/InfoTooltip";
import {
  CommonFlex,
  CommonImage,
  CommonText,
  Container,
  TableText,
} from "../../styledComponents/common";
import { TableItem } from "../../styledComponents/teams";
import {
  getHours,
  getHoursWithSeconds,
  globalDateFormat,
  toHHMMSS,
} from "../../utils/helper";
import { CommImageNameComp } from "../Members/CommonComponents";
import { ClockInOutTableHeader, ClockInOutTableRow } from "./clockInOutStyles";

export const formatClockIn = timestamp => {
  return timestamp
    ? moment.unix(timestamp).format("hh:mm:ss A").toLowerCase()
    : "-";
};

const ClockInOutTable = ({
  isLoading,
  history,
  clockInOutList,
  startDate,
  endDate,
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const minHeight = "46px";

  const startDateDayStartTimestamp = moment(startDate).startOf("day").unix();
  const endDateDayEndTimestamp = moment(endDate).endOf("day").unix();

  const { t } = useTranslation();

  const popupToggle = () => {
    setPopupOpen(prev => !prev);
  };

  // compare using Unix seconds
  const compareToDateRange = time => {
    if (!time) return false;
    const startTs = moment(startDate).startOf("day").unix();
    return time < startTs;
  };

  return (
    <Container style={{ paddingBottom: "20px" }} showBorder={true}>
      {selectedImage && (
        <EvidenceModal
          isOpen={popupOpen}
          toggle={popupToggle}
          evidence={selectedImage}
        />
      )}

      {isLoading ? (
        <ComponentCircleLoader />
      ) : clockInOutList?.length > 0 ? (
        <>
          <ClockInOutTableHeader>
            <TableItem>
              <TableText>{t("member_name")}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t("date")}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t("clock_in")}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t("clock_out")}</TableText>
            </TableItem>
            <TableItem justify="end">
              <TableText align="right">{t("duration")}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t("team")}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t("image")}</TableText>
            </TableItem>
          </ClockInOutTableHeader>

          {clockInOutList.map((instance, index) => {
            const { attendances } = instance;

            return (
              <ClockInOutTableRow isLastItem={index === clockInOutList.length - 1} key={index}>
                {/* Member Name */}
                <CommonFlex direction="column" alignItems="start">
                  <div
                    style={{
                      position: "sticky",
                      top: "0px",
                      minHeight: minHeight,
                      paddingTop: "5px",
                      paddingBottom: "5px",
                    }}
                  >
                    <TableItem
                      onClick={() =>
                        instance?.employee?.id &&
                        history.push(
                          `/user/member-details?id=${instance.employee.id}`,
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <CommImageNameComp
                        imgSource={instance.employee.avatar || avatar5}
                        name={`${instance.employee.first_name}${
                          instance.employee.last_name
                            ? ` ${instance.employee.last_name}`
                            : ``
                        }`}
                        size="36px"
                        fontSize="13px"
                        index={index}
                        color={instance.employee.color}
                        hoverUnderline
                        isOnline={instance.isOnline}
                        userId={instance.employee.id}
                        designation={instance.employee.designation}
                        showAddDesignation={false}
                      />
                    </TableItem>
                  </div>
                </CommonFlex>

                {/* Date */}
                <TableItem>
                  <CommonFlex
                    direction="column"
                    style={{ width: "max-content" }}
                  >
                    {attendances?.map((attendance, index) => (
                      <TableText
                        name
                        key={index}
                        height={minHeight}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: minHeight,
                        }}
                      >
                        {attendance.clockin_timestamp
                          ? globalDateFormat(
                              Math.max(
                                attendance.clockin_timestamp,
                                startDateDayStartTimestamp,
                              ),
                            )
                          : "-"}
                      </TableText>
                    ))}
                    {attendances?.length === 0 && <TableText>-</TableText>}
                  </CommonFlex>
                </TableItem>

                {/* Clock In */}
                <TableItem>
                  {(attendances?.length ?? 0) === 0 ? (
                    // No attendances -> show backup if online, otherwise "-"
                    <TableText
                      name
                      key={index}
                      height={minHeight}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: minHeight,
                      }}
                    >
                      {instance.isOnline &&
                      compareToDateRange(instance.backup_clockin_timestamp)
                        ? formatClockIn(instance.backup_clockin_timestamp)
                        : "-"}
                    </TableText>
                  ) : (
                    // Has attendances -> show only the attendances list
                    <CommonFlex
                      direction="column"
                      style={{ width: "max-content", alignItems: "start" }}
                    >
                      {attendances?.map((attendance, index) => (
                        <TableText
                          name
                          key={index}
                          height={minHeight}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: minHeight,
                            overflow: "visible",
                          }}
                        >
                          <CommonFlex
                            direction="column"
                            gap="0px"
                            style={{ position: "relative" }}
                          >
                            {formatClockIn(attendance.clockin_timestamp)}
                            <CommonText
                              fontSize="10px"
                              color="rgb(32, 190, 173)"
                              style={{ position: "absolute", bottom: "-15px" }}
                            >
                              {attendance.clockin_timestamp &&
                                (moment
                                  .unix(attendance.clockin_timestamp)
                                  .isSame(moment(), "day")
                                  ? "Today"
                                  : attendance.clockin_timestamp <
                                      startDateDayStartTimestamp &&
                                    globalDateFormat(
                                      attendance.clockin_timestamp,
                                    ))}
                              {!attendance.clockin_timestamp && "-"}
                            </CommonText>
                          </CommonFlex>
                        </TableText>
                      ))}
                    </CommonFlex>
                  )}
                </TableItem>

                {/* Clock Out */}
                <TableItem>
                  {attendances?.length === 0 && <TableText>-</TableText>}
                  <CommonFlex
                    direction="column"
                    style={{ width: "max-content", alignItems: "start" }}
                  >
                    {attendances.map((attendance, index) => (
                      <TableText
                        name
                        key={index}
                        height={minHeight}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: minHeight,
                          overflow: "visible",
                        }}
                      >
                        <CommonFlex
                          direction="column"
                          gap="0px"
                          style={{ position: "relative" }}
                        >
                          {formatClockIn(attendance.clockout_timestamp)}
                          <CommonText
                            fontSize="10px"
                            color="rgb(32, 190, 173)"
                            style={{ position: "absolute", bottom: "-15px" }}
                          >
                            {attendance.clockout_timestamp > 0 &&
                              (moment
                                .unix(attendance.clockout_timestamp)
                                .isSame(moment(), "day")
                                ? "Today"
                                : !moment
                                    .unix(attendance.clockout_timestamp)
                                    .isSame(
                                      moment.unix(
                                        Math.max(
                                          attendance.clockin_timestamp,
                                          startDateDayStartTimestamp,
                                        ),
                                      ),
                                      "day",
                                    ) &&
                                  globalDateFormat(
                                    attendance.clockout_timestamp,
                                  ))}
                            {!attendance.clockout_timestamp && "-"}
                          </CommonText>
                        </CommonFlex>
                      </TableText>
                    ))}
                  </CommonFlex>
                </TableItem>

                {/* Duration (right-aligned) */}
                <TableItem justify="end">
                  <CommonFlex
                    direction="column"
                    style={{
                      display: "flex",
                      minHeight: minHeight,
                      width: "100%", // ensures right alignment inside the cell
                    }}
                  >
                    {attendances?.length > 0 ? (
                      attendances.map((attendance, index) => (
                        <TableText
                          name
                          key={index}
                          height={minHeight}
                          align="right" // 👈 aligns text to the right
                          style={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: minHeight,
                            justifyContent: "flex-end", // 👈 pushes flex children to right
                            width: "100%",
                          }}
                        >
                          {attendance.clockout_timestamp
                            ? getHoursWithSeconds(
                                toHHMMSS(
                                  Math.min(
                                    attendance.clockout_timestamp,
                                    endDateDayEndTimestamp,
                                  ) -
                                    Math.max(
                                      attendance.clockin_timestamp,
                                      startDateDayStartTimestamp,
                                    ),
                                ),
                              )
                            : "-"}
                        </TableText>
                      ))
                    ) : (
                      <TableText
                        height={minHeight}
                        align="right" // 👈 aligns placeholder "-"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          minHeight: minHeight,
                          width: "100%",
                        }}
                      >
                        -
                      </TableText>
                    )}
                  </CommonFlex>
                </TableItem>

                {/* Office */}
                <TableItem>
                  {attendances?.length === 0 && <TableText>-</TableText>}
                  <CommonFlex
                    direction="column"
                    style={{
                      width: "100%",
                      alignContent: "start",
                      alignItems: "start",
                    }}
                  >
                    {attendances.map((attendance, index) => (
                      <TableText
                        name
                        key={index}
                        height={minHeight}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          overflow: "wrap",
                          minHeight: minHeight,
                        }}
                      >
                        {attendance.office_name ? attendance.office_name : "-"}
                      </TableText>
                    ))}
                  </CommonFlex>
                </TableItem>

                {/* Image */}
                <TableItem>
                  <CommonFlex
                    direction="column"
                    style={{ width: "max-content" }}
                  >
                    {attendances.map((attendance, index) => {
                      if (!attendance?.selfie) {
                        return (
                          <div
                            key={index}
                            style={{
                              height: "46px",
                            }}
                          ></div>
                        );
                      }
                      return (
                        <CommonImage
                          key={index}
                          height="46px"
                          width="36px"
                          style={{ cursor: `pointer` }}
                          onClick={() => {
                            setSelectedImage(attendance.selfie);
                            popupToggle();
                          }}
                          src={attendance.selfie}
                        />
                      );
                    })}
                  </CommonFlex>
                </TableItem>
              </ClockInOutTableRow>
            );
          })}
        </>
      ) : (
        <NoDataComponent
          title="No Attendance In This Day!"
          padding="5vh 0"
          imageHeight="140px"
        />
      )}
    </Container>
  );
};

export default ClockInOutTable;
